import { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useDebounce } from 'react-use'
import { format } from 'date-fns'
import { DataGrid, GridColDef, GridValidRowModel } from '@mui/x-data-grid'
import { Box, Button, Link, TextField, Typography } from '@mui/material'
import translate from '../../../i18n/translate'
import { OtpListUser } from '../../../modules/services/userService'
import useStyles from './useStyles'

const DEFAULT_PAGE_SIZE = 10

const gridSx = {
  backgroundColor: '#ede9e4',
  borderRadius: '0px',
  border: 'none',
  '.MuiDataGrid-columnHeaders': {
    backgroundColor: '#fc7f03',
    borderRadius: '0px',
    border: '0px',
  },
  '.MuiDataGrid-columnHeaderTitleContainer': {
    color: '#fff',
    fontSize: '15px',
    justifyContent: 'center',
  },
  '.MuiDataGrid-cell': {
    border: '1px solid #fc7f03',
    padding: '5px',
    justifyContent: 'center',
  },
  '.MuiDataGrid-footerContainer': {
    border: '1px solid #fc7f03',
  },
  '.MuiDataGrid-footerContainer p': {
    margin: '0px',
  },
}

export const formatOtpDate = (value: string | null) => (value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '—')

// Paging plus a debounced search that jumps back to the first page
export const useOtpListState = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useDebounce(() => {
    setSearch(searchInput.trim())
    setPage(0)
  }, 400, [searchInput])

  return { page, setPage, pageSize, setPageSize, searchInput, setSearchInput, query: { skip: page * pageSize, take: pageSize, search } }
}

export const OtpUserCell = ({ user }: { user: OtpListUser | null }) => {
  const classes = useStyles()

  if (!user) return <Typography color="text.secondary">{translate('otpListNotRegistered')}</Typography>

  const fullName = [user.name, user.surname].filter(Boolean).join(' ')

  return (
    <Box className={classes.UserCell}>
      <Typography>
        {fullName || '—'}
        {user.isDeleted && <span> ({translate('otpListDeleted')})</span>}
      </Typography>
      {user.email && <Typography variant="body2" color="text.secondary">{user.email}</Typography>}
    </Box>
  )
}

type Props<R extends GridValidRowModel> = {
  title: ReactNode
  addLabel: ReactNode
  onAdd: () => void
  listState: ReturnType<typeof useOtpListState>
  rows: R[]
  count: number
  columns: GridColDef<R>[]
  loading: boolean
  children?: ReactNode
}

// Shared layout of the admin OTP phone lists: back link, title, search, add button and a server-paged grid
const OtpPhonesPage = <R extends GridValidRowModel>({ title, addLabel, onAdd, listState, rows, count, columns, loading, children }: Props<R>) => {
  const classes = useStyles()
  const intl = useIntl()
  const navigate = useNavigate()
  const { page, setPage, pageSize, setPageSize, searchInput, setSearchInput } = listState

  return (
    <div className={classes.Wrapper}>
      <div className={classes.TopRow}>
        <Link color="#fc7f03" className={classes.ToMain} onClick={() => navigate('/')}>
          {translate('commonBackToTheMainPage')}
        </Link>
        <Typography variant="h6">{title}</Typography>
        <TextField
          size="small"
          color="warning"
          label={intl.formatMessage({ id: 'otpListSearch' })}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button color="warning" variant="contained" className={classes.AddButton} onClick={onAdd}>
          {addLabel}
        </Button>
      </div>

      <DataGrid
        sx={gridSx}
        rows={rows}
        rowCount={count}
        columns={columns}
        loading={loading}
        getRowHeight={() => 'auto'}
        disableSelectionOnClick
        pagination
        paginationMode="server"
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize)
          setPage(0)
        }}
        rowsPerPageOptions={[10, 25, 50]}
        autoHeight
        density="comfortable"
      />

      {children}
    </div>
  )
}

export default OtpPhonesPage
